import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { UserService } from 'src/app/services/user.service';
import { User } from 'src/app/models/User';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Role } from 'src/app/models/role.model';
import { RolesService } from 'src/app/services/roles.service';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import {
    MatSlideToggleChange,
    MatSlideToggleModule,
} from '@angular/material/slide-toggle';
import { MatListModule, MatSelectionList } from '@angular/material/list';

import { EditUserComponent } from './edit-user/edit-user.component';
import { MatDialog } from '@angular/material/dialog';
import { AddUserComponent } from './add-user/add-user.component';
import { Page } from 'src/app/models/page.model';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ACTIVE, INACTIVE } from 'src/app/constants/statusConstant';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { NgFor } from '@angular/common';
import { VisibleDirective } from '../../directives/visible.directive';
import { MatCardModule } from '@angular/material/card';
import {
    COUNT,
    NAME,
    OFFSET,
    ROLEID,
    STATUS,
} from 'src/app/constants/queryParamConstants';
import {
    COUNT_ALL,
    ITEM_COUNT,
    OFFSET_START,
} from 'src/app/constants/searchConstants';

interface IUserSearchFormControls {
    search: FormControl<string>;
    roleSearch: FormControl<string>;
}

@Component({
    selector: 'app-admin-page',
    templateUrl: './users.component.html',
    styleUrls: ['./users.component.scss'],
    imports: [
        MatSlideToggleModule,
        MatProgressBarModule,
        MatTooltipModule,
        MatButtonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatOptionModule,
        NgFor,
        MatListModule,
        MatPaginatorModule,
        EditUserComponent,
        VisibleDirective,
        MatCardModule,
    ],
})
export class UsersComponent implements OnInit, AfterViewInit {
    searchForm: FormGroup<IUserSearchFormControls>;
    addUserOpen: boolean;
    formName = 'Add User';
    roleList: Role[] = [];
    levelsList: number[] = [1, 2, 3];
    selectedValue: string;
    userStatus = true;
    loadingUsers = false;
    optionSelected = false;
    actionSuccessful = false;

    pageIndex = 0;

    @ViewChild(MatSelectionList) userSelectList!: MatSelectionList;
    @ViewChild(EditUserComponent) upsertAdminComponent!: EditUserComponent;

    page = new Page<User>();
    queryParams: Map<string, string | number> = new Map<
        string,
        string | number
    >([
        [STATUS, ACTIVE],
        [COUNT, ITEM_COUNT],
        [OFFSET, OFFSET_START],
    ]);

    constructor(
        private userService: UserService,
        private roleService: RolesService,
        private activatedRoute: ActivatedRoute,
        private dialog: MatDialog,
        private router: Router,
    ) {}

    ngOnInit(): void {
        this.initializeSearchForm();
        this.populateFilters();
        this.getUsers();
        this.getRoles();
    }

    debouceTimeMS = 500;

    ngAfterViewInit(): void {
        this.userSelectList.selectionChange.subscribe((change) => {
            if (!this.optionSelected) {
                this.optionSelected = true;
            }

            this.userService
                .get([change.options[0].value.id as string])
                .subscribe((user: User) => {
                    this.upsertAdminComponent.user = user;
                    this.upsertAdminComponent.initializeForm();
                });
        });

        this.searchForm.controls.search.valueChanges
            .pipe(debounceTime(this.debouceTimeMS), distinctUntilChanged())
            .subscribe((value) => {
                if (value === '' || value === null) {
                    if (this.queryParams.get(NAME)) {
                        this.queryParams.delete(NAME);
                    }
                }

                setTimeout(() => {
                    this.upsertAdminComponent.resetComponent();
                    this.queryParams.set(NAME, value);
                    this.changePageIndex(0);
                }, this.debouceTimeMS);
            });
    }

    usersLoadingToggle(event: boolean): void {
        this.loadingUsers = event;
    }

    toggleActiveInactive(event: MatSlideToggleChange): void {
        this.userStatus = event.checked;
        this.optionSelected = false;

        this.changePageIndex(0);
    }

    changePage(event: PageEvent): void {
        this.changePageIndex(event.pageIndex);
    }

    changePageIndex(index: number) {
        this.pageIndex = index;
        this.getUsers();
    }

    openAddDialog(): void {
        const dialog = this.dialog.open(AddUserComponent, {
            disableClose: true,
            data: this.roleList,
        });

        //We want to get a list of the users again when the dialog close with a true status.
        dialog.afterClosed().subscribe((result) => {
            if (result === true) {
                this.getUsers();
            }
        });
    }

    roleSelectionChange(): void {
        if (this.searchForm.controls.roleSearch) {
            this.queryParams.set(
                ROLEID,
                this.searchForm.controls.roleSearch.value,
            );
            this.getUsers();
        }
    }

    initializeSearchForm(): void {
        this.searchForm = new FormGroup({
            search: new FormControl(''),
            roleSearch: new FormControl(''),
        });
    }

    onUpdateSuccessLoad(event?: boolean): void {
        if (event) {
            this.optionSelected = false;
        }
        this.getUsers();
    }

    private getUsers() {
        this.loadingUsers = true;
        this.setQueryParams();

        this.userService
            .get([], this.queryParams)
            .subscribe((page: Page<User>) => {
                this.page = page;
                this.loadingUsers = false;
            });
    }

    private getRoles(): void {
        this.roleService
            .get(
                [],
                new Map<string, unknown>([
                    [STATUS, ACTIVE],
                    [COUNT, COUNT_ALL],
                ]),
            )
            .subscribe((res: Page<Role>) => {
                this.roleList = res.items;
            });
    }

    private setQueryParams(): void {
        // Set the offset param based on the pageIndex and size
        if (this.pageIndex) {
            this.queryParams.set(
                OFFSET,
                this.pageIndex * this.page.countRequested,
            );
        } else {
            this.queryParams.set(OFFSET, OFFSET_START);
        }

        if (this.userStatus) {
            this.queryParams.set(STATUS, ACTIVE);
        } else {
            this.queryParams.set(STATUS, INACTIVE);
        }

        this.updateUrl();
    }

    private populateFilters(): void {
        const params: Params = this.activatedRoute.snapshot.queryParams;

        if (STATUS in params) {
            this.userStatus = params[STATUS] !== INACTIVE;
        }

        if (NAME in params) {
            this.queryParams.set(NAME, params[NAME]);
            this.searchForm.controls.search.setValue(params[NAME] as string);
        }

        if (ROLEID in params) {
            this.searchForm.controls.roleSearch.setValue(
                params[ROLEID] as string,
            );
        }
    }

    private updateUrl(): void {
        const queryParams: Params = {};

        if (this.userStatus !== null && this.userStatus !== undefined) {
            queryParams[STATUS] = this.userStatus ? ACTIVE : INACTIVE;
        }

        const nameValue = this.searchForm.controls.search.value;
        if (nameValue) {
            queryParams[NAME] = nameValue;
        }

        const roleValue = this.searchForm.controls.roleSearch.value;
        if (roleValue) {
            queryParams[ROLEID] = roleValue;
        }

        void this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: queryParams,
        });
    }
}
